import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Modal, TextInput, Checkbox, InfoIcon } from '@design-system';
import { Tooltip } from '@chakra-ui/react';
import { translations } from 'translations';
import { useTranslation } from 'react-i18next';
import { useFetchAll as useFetchContacts } from '@pages/ContactsPage/hooks';
import { WLContext } from '@contexts';
import { Select } from '@design-system/inputs/BaseInputs';
import { IContact } from '@cincel/client';
import { useSession } from 'store/session';
import fp from 'lodash/fp';
import { RfcValidationField } from './RfcValidationField';

import { logger } from 'helpers/logger';

export const SignerModal = ({
  setShowModal,
  isOpen,
  isEditingSigner,
  handleEditSignerSuccess,
  handleSignerSuccess,
  setIsEditingSigner,
  isAddSignerDisabled,
  signerData,
  handleBuildSigner,
  hasAvailableKyc,
  isKycEnabled,
  contactsList,
  setContactsList,
  handleContactsSignerSuccess,
  addSignerToAgenda,
  setaddSignerToAgenda,
  features,
}) => {
  logger.log('SignerModal()', { signerData, isEditingSigner });
  const { color } = useContext(WLContext);
  logger.log('FEATURES:', features);

  const { data: contacts } = useFetchContacts({ pageSize: 100000 });

  const { t } = useTranslation();

  const [tab, setTab] = useState<'standard' | 'signers'>('standard');

  // Key prop to force re-render when signerData changes
  const checkboxKey = useMemo(
    () =>
      `${signerData?.email || 'new'}-${isEditingSigner ? 'edit' : 'new'
      }-${Date.now()}`,
    [signerData?.email, isEditingSigner, isOpen],
  );

  const shouldDisableRfcValidation = (signerData) => {
    return !signerData?.advanced || signerData?.typed || signerData?.autograph;
  };

  const { credits } = useSession();

  const hasKYCCredits = useMemo(
    () => credits?.kyc?.spent >= credits?.kyc?.got,
    [credits?.kyc],
  );

  return (
    <Modal
      showModal={isOpen}
      subtitle=""
      width="md:w-7/12"
      setShowModal={setShowModal}
      onSuccess={() => {
        if (!fp.isEmpty(contactsList)) {
          const data = contactsList?.map((item) => ({
            email: item?.email,
            name: item?.name,
          }));
          handleContactsSignerSuccess(data);
        }

        const handleSuccess = () => {
          setIsEditingSigner(false);
          setTab('standard');
        };

        isEditingSigner
          ? handleEditSignerSuccess(handleSuccess)
          : handleSignerSuccess(handleSuccess);
      }}
      isDisabled={
        isAddSignerDisabled() ||
        (!signerData.autograph && !signerData.advanced && !signerData.typed)
      }
      title={
        isEditingSigner
          ? t(translations.MODALS.SIGNER_TITLE_MODIFY)
          : t(translations.MODALS.SIGNER_TITLE_ADD)
      }
      closeOnSuccess={false}
      handleCancel={() => {
        setTab('standard');
        setContactsList([]);
        setIsEditingSigner(false);
        setShowModal();
      }}
    >
      {!isEditingSigner ? (
        <>
          <div className="mb-5 flex">
            <div
              className="flex-1 cursor-pointer border-b-2 pb-2 text-sm font-medium"
              style={tab === 'standard' ? { borderColor: color } : {}}
              onClick={() => setTab('standard')}
            >
              <p className="mr-4">
                {t(translations.MODALS.ADD_FROM_NAME_AND_EMAIL)}
              </p>
            </div>
            <div
              className="flex-1 cursor-pointer border-b-2 pb-2 text-sm font-medium"
              style={tab === 'signers' ? { borderColor: color } : {}}
              onClick={() => {
                handleBuildSigner('email', '');
                handleBuildSigner('name', '');
                setTab('signers');
              }}
            >
              <p>{t(translations.MODALS.ADD_FROM_CONTACTS)}</p>
            </div>
          </div>
          {tab === 'standard' ? (
            <div className="flex flex-col">
              <div className="mb-5 flex flex-col lg:flex-row">
                <div className="mb-4 flex-1 lg:mb-0 lg:mr-5">
                  <p className="mb-1 text-paragraph font-medium text-gray-300">
                    {t(translations.INPUTS.NAME)}
                  </p>
                  <TextInput
                    id="name"
                    isFullWidth
                    name="name"
                    defaultValue={signerData?.name}
                    value={signerData?.name}
                    onChange={(e) => handleBuildSigner('name', e.target.value)}
                  />
                </div>

                <div className="flex-1">
                  <p className="mb-1 text-paragraph font-medium text-gray-300">
                    {t(translations.INPUTS.EMAIL)}
                  </p>
                  <TextInput
                    isFullWidth
                    id="email"
                    name="email"
                    defaultValue={signerData?.email}
                    value={signerData?.email}
                    onChange={(e) => handleBuildSigner('email', e.target.value)}
                  />
                </div>
              </div>

              <div className="flex-col">
                <div className="mb-5 flex flex-col">
                  <p className="mb-1 text-paragraph font-medium text-gray-300">
                    {t(translations.DOCUMENT_PAGES.SIGNATURE_TYPE)}
                  </p>
                  <div className="flex flex-row flex-wrap">
                    <Checkbox
                      key={`${checkboxKey}-advanced`}
                      defaultValue={signerData?.advanced}
                      onChange={(e) => handleBuildSigner('advanced', e)}
                      className="mt-[10px]"
                      label={`${t(translations.COMMON.ADVANCED)}`}
                    />
                    <Checkbox
                      key={`${checkboxKey}-autograph`}
                      defaultValue={signerData?.autograph}
                      onChange={(e) => handleBuildSigner('autograph', e)}
                      className="ml-6 mt-[10px]"
                      label={`${t(translations.COMMON.AUTOGRAPH)}`}
                    />
                    <Checkbox
                      key={`${checkboxKey}-typed`}
                      defaultValue={signerData?.typed}
                      onChange={(e) => handleBuildSigner('typed', e)}
                      className="ml-6 mt-[10px]"
                      label={`${t(translations.COMMON.TYPED)}`}
                    />
                  </div>
                  <RfcValidationField
                    checkboxKey={checkboxKey}
                    signerData={signerData}
                    handleBuildSigner={handleBuildSigner}
                    shouldDisableRfcValidation={shouldDisableRfcValidation}
                    t={t}
                    features={features}
                    mode="create"
                  />
                </div>
                <Checkbox
                  key={`${checkboxKey}-agenda`}
                  defaultValue={addSignerToAgenda}
                  onChange={(e) => setaddSignerToAgenda(e)}
                  className="mt-[10px]"
                  label={t(translations.MODALS.ADD_SIGNER_TO_DIRECTORY)}
                />
                {isKycEnabled() && hasAvailableKyc() && (
                  <Checkbox
                    key={`${checkboxKey}-kyc`}
                    defaultValue={signerData?.identity_verification}
                    onChange={(e) =>
                      handleBuildSigner('identity_verification', e)
                    }
                    className="mt-[10px]"
                    label={`${t(translations.MODALS.SIGNER_ASK_LIFE_PROOF)}`}
                  />
                )}
                {!!features?.can_force_geolocation?.enabled && (
                  <Checkbox
                    key={`${checkboxKey}-geolocation`}
                    label={`${t(translations.INPUTS.FORCE_SIGNER_GEOLOCATION)}`}
                    className="mt-[10px]"
                    defaultValue={signerData?.user_coordinates}
                    onChange={(e) => handleBuildSigner('user_coordinates', e)}
                  />
                )}
                <Checkbox
                  key={`${checkboxKey}-notification`}
                  defaultValue={signerData?.notification}
                  onChange={(e) => handleBuildSigner('notification', e)}
                  className="mt-[10px]"
                  label={t(translations.MODALS.ADD_SIGNER_NOTIFICATION)}
                />
              </div>
            </div>
          ) : null}
          {tab === 'signers' ? (
            <div className="flex flex-col">
              <Select
                className="mb-4 flex-1"
                isMulti
                options={contacts?.data?.map((item: IContact) => ({
                  label: item?.email,
                  value: item,
                }))}
                onChange={setContactsList}
                placeholder={t(translations.MODALS.CHOOSE_SIGNERS)}
                primaryColor={color}
                size="md"
                value={contactsList}
                maxMenuHeight={70}
              />

              <div className="flex-col">
                {isKycEnabled() && hasAvailableKyc() && (
                  <Checkbox
                    key={`${checkboxKey}-contacts-kyc`}
                    label={`${t(translations.MODALS.SIGNER_ASK_LIFE_PROOF)}`}
                    className="mt-[10px]"
                    defaultValue={signerData?.identity_verification}
                    onChange={(e) =>
                      handleBuildSigner('identity_verification', e)
                    }
                  />
                )}
                {!!features?.can_force_geolocation?.enabled && (
                  <Checkbox
                    key={`${checkboxKey}-contacts-geolocation`}
                    label={`${t(translations.INPUTS.FORCE_SIGNER_GEOLOCATION)}`}
                    className="mt-[10px]"
                    defaultValue={signerData?.user_coordinates}
                    onChange={(e) => handleBuildSigner('user_coordinates', e)}
                  />
                )}
                <Checkbox
                  key={`${checkboxKey}-contacts-notification`}
                  label={t(translations.MODALS.ADD_SIGNER_NOTIFICATION)}
                  className="mt-[10px]"
                  defaultValue={signerData?.notification}
                  onChange={(e) => handleBuildSigner('notification', e)}
                />
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex flex-col">
          <div className="mb-5 flex flex-col lg:flex-row">
            <div className="mb-4 flex-1 lg:mb-0 lg:mr-5">
              <p className="mb-1 text-paragraph font-medium text-gray-300">
                {t(translations.INPUTS.NAME)}
              </p>
              <TextInput
                id="name"
                isFullWidth
                name="name"
                defaultValue={signerData?.name}
                value={signerData?.name}
                onChange={(e) => handleBuildSigner('name', e.target.value)}
              />
            </div>

            <div className="flex-1">
              <p className="mb-1 text-paragraph font-medium text-gray-300">
                {t(translations.INPUTS.EMAIL)}
              </p>
              <TextInput
                isFullWidth
                id="email"
                name="email"
                defaultValue={signerData?.email}
                value={signerData?.email}
                onChange={(e) => handleBuildSigner('email', e.target.value)}
              />
            </div>
          </div>

          <div className="mb-3 flex flex-col">
            <p className="mb-1 text-paragraph font-medium text-gray-300">
              {t(translations.DOCUMENT_PAGES.SIGNATURE_TYPE)}
            </p>
            <div className="flex flex-row flex-wrap">
              <Checkbox
                key={`${checkboxKey}-edit-advanced`}
                defaultValue={signerData?.advanced}
                onChange={(e) => handleBuildSigner('advanced', e)}
                className="mt-[10px]"
                label={`${t(translations.COMMON.ADVANCED)}`}
              />
              <Checkbox
                key={`${checkboxKey}-edit-autograph`}
                defaultValue={signerData?.autograph}
                onChange={(e) => handleBuildSigner('autograph', e)}
                className="ml-6 mt-[10px]"
                label={`${t(translations.COMMON.AUTOGRAPH)}`}
              />
              <Checkbox
                key={`${checkboxKey}-edit-typed`}
                defaultValue={signerData?.typed}
                onChange={(e) => handleBuildSigner('typed', e)}
                className="ml-6 mt-[10px]"
                label={`${t(translations.COMMON.TYPED)}`}
              />
            </div>

            <RfcValidationField
              checkboxKey={checkboxKey}
              signerData={signerData}
              handleBuildSigner={handleBuildSigner}
              shouldDisableRfcValidation={shouldDisableRfcValidation}
              t={t}
              features={features}
              mode="edit"
            />
          </div>

          <div className="flex-col">
            {isKycEnabled() && hasAvailableKyc() && (
              <Checkbox
                key={`${checkboxKey}-edit-kyc`}
                defaultValue={signerData?.identity_verification}
                onChange={(e) => handleBuildSigner('identity_verification', e)}
                className="mt-[10px]"
                label={`${t(translations.MODALS.SIGNER_ASK_LIFE_PROOF)}`}
              />
            )}
            {!!features?.can_force_geolocation?.enabled && (
              <Checkbox
                key={`${checkboxKey}-edit-geolocation`}
                label={`${t(translations.INPUTS.FORCE_SIGNER_GEOLOCATION)}`}
                className="mt-[10px]"
                defaultValue={signerData?.user_coordinates}
                onChange={(e) => handleBuildSigner('user_coordinates', e)}
              />
            )}
            <Checkbox
              key={`${checkboxKey}-edit-notification`}
              defaultValue={signerData?.notification}
              onChange={(e) => handleBuildSigner('notification', e)}
              className="mt-[10px]"
              label={t(translations.MODALS.ADD_SIGNER_NOTIFICATION)}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};
